import {Injectable} from '@angular/core';
import {Headers, Http, ResponseContentType} from '@angular/http';

import 'rxjs/add/operator/toPromise';
import 'rxjs/Rx' ;

import {Person} from '../Model/Person';
import {PersonLunch} from '../Model/PersonLunch';
import {StandardDays} from '../Model/StandardDays';
import {Role} from '../Model/Role';

import {saveAs} from 'file-saver/FileSaver';
import {Guest} from '../Model/Guest';
import {DateTimeValidation} from '../Model/DateTimeValidation';

@Injectable()
export class DataService {

  public personName = '';
  public role: Role;
  private personUrl = 'person/';  // URL to web API
  private lunchUrl = 'lunch/'; // URL to lunch
  private standardDays = 'standardDays/'; // URL to lunch
  private kantineUrl = 'kantine/';  // URL to kantine
  private exportUrl = 'export/'; // URL for exports

  private headers = new Headers({'Content-Type': 'application/json'});

  constructor(private http: Http) {
  }

  /**
   * get the currently logged in person
   * @returns {Promise<Person>}
   */
  getCurrentPerson(): Promise<Person> {
    return this.http.get(this.personUrl + 'currentLoggedIn', {headers: this.headers})
      .toPromise()
      .then((response) => {
        const personResponse = response.json() as Person;
        this.personName = personResponse.name;
        this.role = personResponse.role;
        return response.json() as Person;
      })
      .catch(this.handleError);
  }

  /**
   * login the user with name and password
   * @param {String} name
   * @param {String} password
   * @returns {Promise<Headers>} these headers will be added to the {@link headers} variable
   */
  login(name: String, password: String): Promise<Headers> {
    const _this = this;
    return this.http.post('login', JSON.stringify({name: name, password: password}), {headers: this.headers})
      .toPromise()
      .then((res) => {
        res.headers.forEach(function (val, key) {
          if (key === 'authorization') {
            _this.headers.append('authorization', val[0]);
          }
        });
        return res.headers;
      })
      .catch(this.handleError);
  }

  /**
   * logout will remote the header and reset all the user variables
   * @returns {Promise<any>} doesn't really return anything, Promise is used for returning succes or a catch if something went wrong
   */
  logout(): Promise<any> {
    this.headers.delete('authorization');
    return this.http.get('logout', {headers: this.headers})
      .toPromise()
      .then(() => {
        this.personName = '';
      }).catch(() => {
        this.personName = '';
      })
  }

  /**
   * get all personLunches from one {@link Person}
   * @returns {Promise<PersonLunch[]>}
   */
  getPersonLunches(): Promise<PersonLunch[]> {
    console.log('getLunches');
    return this.http.get(this.lunchUrl + 'getAll', {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch[])
      .catch(this.handleError);
  }

  /**
   * subscribe to a specific day as a {@link Person}
   * @param {Date} date the date when the person subscribes himself to a lunch
   * @param {boolean} goLunch Does the person go to lunch or does he not? subscribing or unsubscribing
   * @param time {string} time of lunch
   * @param {number} guestAmount Amount of guests your taking with you
   * @param {string} guestInformation extra information about the guests
   * @returns {Promise<PersonLunch>} returned the just added {@link PersonLunch}
   */
  subscribeToDay(date: Date, goLunch: boolean, time: string, guestAmount: number, guestInformation: string, wantSnack: boolean): Promise<PersonLunch> {
    const personLunch = {
      id: 0,
      date: date,
      didLunch: false,
      didLunchTime: null,
      lunch: goLunch,
      time: '12' + time,
      person: null,
      guestAmount: guestAmount,
      guestInformation: guestInformation,
      wantSnack: wantSnack
    };
    return this.http.post(this.lunchUrl + 'subscribeToDay', personLunch, {headers: this.headers})
      .toPromise()
      .then(res => res.json() as PersonLunch)
      .catch(this.handleError);
  }

  /**
   * get standard days from currently logged in {@link Person}
   * @returns {Promise<StandardDays>} with the standardays object
   */
  getStandardDays(): Promise<StandardDays> {
    return this.http.get(this.standardDays + 'getStandardDays', {headers: this.headers})
      .toPromise()
      .then(response => response.json() as StandardDays)
      .catch(this.handleError);
  }

  /**
   * set the standard days for the logged in {@link Person}
   * @param {StandardDays} standardDays that should be set
   * @returns {Promise<StandardDays>} the just saved {@link StandardDays} object
   */
  setStandardDays(standardDays: StandardDays): Promise<StandardDays> {
    return this.http
      .post(this.standardDays + 'setStandardDays', JSON.stringify(standardDays), {headers: this.headers})
      .toPromise()
      .then(response => response.json() as StandardDays)
      .catch(this.handleError);
  }

  /**
   * get all personLunches from today where {@link PersonLunch.didLunch} is true
   * @returns {Promise<PersonLunch[]>} all PersonLunches with didLunch: true
   */
  getPersonLunchesOfToday(): Promise<PersonLunch[]> {
    return this.http.get(this.kantineUrl + 'getFromToday', {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch[])
      .catch(this.handleError);
  }

  /**
   * get all personLunches from today where {@link PersonLunch.didLunch} is true
   * @returns {Promise<PersonLunch[]>} all PersonLunches with didLunch: true
   */
  getPersonLunchesLiveStatistics(): Promise<PersonLunch[]> {
    return this.http.get(this.lunchUrl + 'getFromToday', {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch[])
      .catch(this.handleError);
  }


  /**
   * Set the vacationperiod of the currently logged in {@link Person}
   * @param {Date} fromDate start of the vacation
   * @param {Date} toDate end of the vacation
   * @returns {Promise<any>} returns a ok if the data is saved
   */
  vacationPeriod(fromDate: Date, toDate: Date): Promise<any> {
    const dates = [fromDate, toDate];
    return this.http.post(this.lunchUrl + 'vacation', dates, {headers: this.headers})
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  changePassword(newPassword: String): Promise<any> {
    return this.http.post(this.personUrl + 'changePassword', newPassword, {headers: this.headers})
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  /**
   * get Amount of {@link PersonLunch} today have {@link PersonLunch.lunch} true
   * @returns {Promise<number>} the number of {@link PersonLunch} that will take lunch today
   */
  getAmountOfLunchesToday(): Promise<number> {
    return this.http.get(this.kantineUrl + 'getAmountOfLunchesToday', {headers: this.headers})
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }

  /**
   * Get the statistics of the whole current week
   * @returns {Promise<PersonLunch[]>} all {@link PersonLunch} this week
   */
  getStatisticsOfThisWeek(): Promise<PersonLunch[]> {
    return this.http.get(this.kantineUrl + 'personLunchesThisWeek', {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch[])
      .catch(this.handleError);
  }

  /**
   * Get the statistics of a whole week with a given date
   * @param {Date} date will get all statistics where the given date is in
   * @returns {Promise<PersonLunch[]>} all {@link PersonLunch} in the given week
   */
  getStatisticsOfWeekWithDate(date: Date): Promise<PersonLunch[]> {
    return this.http.post(this.kantineUrl + 'personLunchesFromWeek', date, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch[])
      .catch(this.handleError);
  }

   checkDateAndHourValidity(date: string): Promise<DateTimeValidation> {
    return this.http.get(`${this.lunchUrl}checkDateAndHourValidity?date=${date}`, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as DateTimeValidation)
      .catch(this.handleError);
  }

  /**
   * search a person with the given searchValue
   * @param {string} searchValue is the string which will be used to search for {@link Person}
   * @returns {Promise<Person[]>} All found {@link Person}
   */
  searchPerson(searchValue: string): Promise<Person[]> {
    return this.http.post(this.kantineUrl + 'personSearch', searchValue, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person[])
      .catch(this.handleError);
  }

  /**
   * search a person without druppelId with the given searchValue
   * @param {string} searchValue is the string which will be used to search for {@link Person}
   * @returns {Promise<Person[]>} All found {@link Person}
   */
  searchPersonForDruppel(searchValue: string): Promise<Person[]> {
    return this.http.post(this.kantineUrl + 'personSearchDruppel', searchValue, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person[])
      .catch(this.handleError);
  }


  /**
   * Set didLunch to true for a {@link Person} for today's date
   * @param {Person} person which person will be set to true
   * @returns {Promise<PersonLunch>} The {@link PersonLunch} that is just adjusted or set
   */
  addDruppelToPerson(person: Person): Promise<PersonLunch> {
    return this.http.post(this.kantineUrl + 'addDruppelToPerson', person, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch)
      .catch(this.handleError);
  }


  /**
   * Set didLunch to true for a {@link Person} for today's date
   * @param {Person} person which person will be set to true
   * @returns {Promise<PersonLunch>} The {@link PersonLunch} that is just adjusted or set
   */
  addPersonDefinitive(person: Person): Promise<PersonLunch> {
    return this.http.post(this.lunchUrl + 'definitiveHttps', person.druppelId, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch)
      .catch(this.handleError);
  }

  addPersonDefinitivePerDate(person: Person, date: Date): Promise<PersonLunch> {
    const data = {Person: person.name, date: date};
    return this.http.post(this.lunchUrl + 'definitiveHttpsPerDate', data, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch)
      .catch(this.handleError);
  }

  deletePerson(person: Person): Promise<Person> {
    return this.http.post(this.lunchUrl + 'deletePersonFromLunchToday', person, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person)
      .catch(this.handleError);
  }

  /**
   * addGuest to a personLunch
   * @param {number} guestAmount how much guests?
   * @param {string} guestCompany extra information about the company the guests are coming from
   * @param {Person} searchedPerson Which {@link Person} has invited the guests
   * @param {Date} date on which date do the guests come?
   * @returns {Promise<PersonLunch>} the saved {@link PersonLunch}
   */
  addGuests(guestAmount: number, guestCompany: string, searchedPerson: Person, date: Date): Promise<PersonLunch> {
    let personLunch: PersonLunch;
    personLunch = {
      id: 1,
      date: date,
      didLunch: false,
      didLunchTime: null,
      lunch: true,
      time: null,
      person: searchedPerson,
      guestAmount: guestAmount,
      guestInformation: guestCompany,
      wantSnack: false
    };
    console.log(personLunch);
    return this.http.post(this.lunchUrl + 'addGuest', personLunch, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch)
      .catch(this.handleError);
  }

  /**
   * get the guests from a specific date
   * @param {Date} date
   * @returns {Promise<PersonLunch[]>}
   */
  getGuests(date: Date): Promise<PersonLunch[]> {
    return this.http.post(this.lunchUrl + 'getGuests', date, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch[])
      .catch(this.handleError);
  }

  /**
   * Remove the guest from a {@link PersonLunch}
   * @param {PersonLunch} personLunch
   * @returns {Promise<PersonLunch>}
   */
  removeGuest(personLunch: PersonLunch): Promise<PersonLunch> {
    return this.http.post(this.lunchUrl + 'removeGuest', personLunch, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as PersonLunch)
      .catch(this.handleError);
  }

  /**
   * Get the data of Telfort persons between certain dates
   * @returns {Promise<PersonLunch>}
   * @param date
   * @param extension
   */
  getTelfortExport(date: Date[], extension: string) {
    return this.http.post(this.exportUrl + 'getTelfortTotalCosts' + extension, date, {
      headers: this.headers,
      responseType: ResponseContentType.Blob
    }).subscribe(
      (data: any) => this.downloadFile(data),
      error => console.log('Error downloading the file.'),
      () => console.info('OK'));
  }

  /**
   * Get the data of Inther persons between certain dates
   * @param {Date[]} date
   * @param {string} extension
   * @returns {Subscription}
   */
  getIntherExportPerPerson(date: Date[], extension: string) {
    return this.http.post(this.exportUrl + 'getIntherPerPerson' + extension, date, {
      headers: this.headers,
      responseType: ResponseContentType.Blob
    }).subscribe(
      (data: any) => this.downloadFile(data),
      error => console.log('Error downloading the file.'),
      () => console.info('OK'));
  }

  /**
   * Get the data of Inther persons between certain dates
   * @param {Date[]} date
   * @param {string} extension
   * @returns {Subscription}
   */
  getTelfortExportPerPerson(date: Date[], extension: string) {
    return this.http.post(this.exportUrl + 'getTelfortPerPerson' + extension, date, {
      headers: this.headers,
      responseType: ResponseContentType.Blob
    }).subscribe(
      (data: any) => this.downloadFile(data),
      error => console.log('Error downloading the file.'),
      () => console.info('OK'));
  }

  exportToAfas(date: Date[]) {
    return this.http.post(this.exportUrl + 'ExportMonthToAfas', date, {headers: this.headers});
  }

  newTelfortPerson(person: Person) {
    return this.http.post(this.personUrl + 'addTelfortPerson', person, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person)
      .catch(this.handleError);
  }

  getTelfortEmployees() {
    return this.http.get(this.personUrl + 'getTelfortEmployees', {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person[])
      .catch(this.handleError);
  }

  setTelfortEmployeeDisabled(person: Person) {
    return this.http.post(this.personUrl + 'setTelfortEmployeeDisabled', person, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person)
      .catch(this.handleError);
  }


  resetPassword(person: Person) {
    return this.http.post(this.personUrl + 'resetPassword', person, {headers: this.headers})
      .toPromise()
      .then(response => response.json() as Person)
      .catch(this.handleError);
  }

  /**
   * Download the file returned from the server
   * @param {Response} data
   */
  downloadFile(data: Response): void {
    console.log(data);
    let fileBlob = data.blob();
    var blob = new Blob([fileBlob], {type: data.headers.get('content-type')});
    saveAs(blob);
  }

  addGuest(guest: Guest): void {
    this.http.post(this.kantineUrl + 'addGuest', guest, {headers: this.headers})
      .toPromise()
  }

  addNote(message: String): void {
    this.http.post(this.kantineUrl + 'addNote', message, {headers: this.headers}).toPromise();
  }

  /**
   * Print the error to the log when a requests goes wrong
   * @param error
   * @returns {Promise<any>}
   */
  private handleError(error: any): Promise<any> {
    if (error.status === 403) {
      this.headers = null;
      this.personName = '';
      this.role = null;
    }
    console.log(error.status);
    console.error('Error', error); // for demo purposes only
    return Promise.reject(error.message || error);
  }

  getAmountOfSnacksToday(): Promise<number> {
    return this.http.get(this.kantineUrl + 'getAmountOfSnacksToday', {headers: this.headers})
      .toPromise()
      .then(response => response.json())
      .catch(this.handleError);
  }
}



// WEBPACK FOOTER //
// ./src/app/Services/data.service.ts